import GeneralPromptStyles from 'common/Prose/Prompts/Prompt/Prompt.module.scss'
import { EmbedURL, Github, Integration, Jira } from 'proseShared/IntegrationsIcons'
import { Node as ProseNode } from 'prosemirror-model'
import { type EditorView } from 'prosemirror-view'
import { useEffect, useRef, useState } from 'react'
import { GITHUB_INTEGRATION_REGEX, JIRA_INTEGRATION_REGEX } from 'screens/ProseEdit/utils'
import { GenericEmbed, Position } from 'types'
import Styles from '../Prompt.module.scss'
import { adjustHorizontalPosition, adjustSidebarMaxHeight } from '../utils'
import { EmbedFromURL } from './EmbedFromURL'
import { GithubPreview } from './GithubPreview'
import { JiraPreview } from './JiraPreview'
import PromptFooter from './PromptFooter'
import Sidebar from './Sidebar'
import { createRoot } from 'react-dom/client'

type GithubEmbed = {
  provider: 'Github'
  type: 'issue' | 'pull'
  url: string
}

type JiraEmbed = {
  provider: 'Jira'
  type: 'issue'
  issueId: string
  link: string
}

type EmbedUnion = GenericEmbed | GithubEmbed | JiraEmbed

export function isGithubEmbed(embed: EmbedUnion): embed is GithubEmbed {
  return embed.provider === 'Github'
}

export function isJiraEmbed(embed: EmbedUnion): embed is JiraEmbed {
  return embed.provider === 'Jira'
}

interface Props {
  // this is empty when user clicks "Cancel" within the prompt
  onSubmit: (embedCode?: EmbedUnion) => void
  preselectedIntegration?: Integration
}

const PromptContent = ({ onSubmit, preselectedIntegration }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const [activeIntegration, setActiveIntegration] = useState<Integration>(preselectedIntegration || EmbedURL)
  const [stagedEmbed, setStagedEmbed] = useState<GenericEmbed>()
  const [inputValue, setInputValue] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const mouseOutside = ({ target }: MouseEvent) => {
      if (!containerRef.current?.contains(target as Node)) {
        onSubmit()
      }
    }

    window.addEventListener('mousedown', mouseOutside)

    return () => window.removeEventListener('mousedown', mouseOutside)
  }, [onSubmit])

  const onCancel = () => onSubmit(undefined)

  const onSuccess = async () => {
    switch (activeIntegration.name) {
      case 'Github':
        const githubRegexResult = GITHUB_INTEGRATION_REGEX.exec(inputValue)

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let type = githubRegexResult!.groups!.resourceName
        if (type === 'issues') type = 'issue'
        onSubmit({ type: type as 'issue' | 'pull', provider: 'Github', url: inputValue })
        break
      case 'Jira':
        const jiraRegexResult = JIRA_INTEGRATION_REGEX.exec(inputValue)

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let issueId = jiraRegexResult!.groups!.id
        onSubmit({ type: 'issue', provider: 'Jira', link: inputValue, issueId })
        break
      default:
        onSubmit(stagedEmbed)
        break
    }
  }

  const onActiveIntegrationChange = (integration: Integration) => {
    setStagedEmbed(undefined)
    setInputValue('')
    setErrorMessage('')
    setActiveIntegration(integration)
    inputRef.current?.focus()
  }

  return (
    <div className={Styles.mediaUploadContainer} ref={containerRef}>
      <Sidebar activeIntegration={activeIntegration} setActiveIntegration={onActiveIntegrationChange} />

      <div className={Styles.mediaUploadMain}>
        {(() => {
          const githubRegexResult = GITHUB_INTEGRATION_REGEX.exec(inputValue)
          let currentIntegrationName = activeIntegration.name

          if (
            currentIntegrationName !== 'Github' &&
            (githubRegexResult !== null || /^(https?:\/\/)github\.com/.test(inputValue))
          ) {
            currentIntegrationName = 'Github'
            setActiveIntegration(Github)
          }

          const jiraRegexResult = JIRA_INTEGRATION_REGEX.exec(inputValue)

          if (currentIntegrationName !== 'Jira' && jiraRegexResult?.groups?.id) {
            currentIntegrationName = 'Jira'
            setActiveIntegration(Jira)
          }

          switch (currentIntegrationName) {
            case 'Github':
              return (
                <GithubPreview
                  inputRef={inputRef}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  regexResult={githubRegexResult}
                  setStagedEmbed={setStagedEmbed}
                  onSuccess={onSuccess}
                />
              )
            case 'Jira':
              /* eslint-disable no-fallthrough */
              // If there's no issueId we should render the default Preview
              if (jiraRegexResult?.groups?.id) {
                return (
                  <JiraPreview
                    inputRef={inputRef}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    issueId={jiraRegexResult.groups.id}
                    setStagedEmbed={setStagedEmbed}
                    onSuccess={onSuccess}
                  />
                )
              }
            default:
              /* eslint-enable no-fallthrough */
              return (
                <EmbedFromURL
                  inputRef={inputRef}
                  stagedEmbed={stagedEmbed}
                  setStagedEmbed={setStagedEmbed}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  onSuccess={onSuccess}
                />
              )
          }
        })()}

        <PromptFooter
          onSuccess={onSuccess}
          onCancel={onCancel}
          loading={false}
          enabled={!!stagedEmbed || activeIntegration.name === 'Github'}
        />
      </div>
    </div>
  )
}

export const openEmbedPrompt = async ({
  view,
  position,
  preselectedIntegration,
  centered,
}: {
  view: EditorView
  position?: { left: number; top: number }
  preselectedIntegration?: Integration
  centered?: boolean
}): Promise<EmbedUnion | undefined> => {
  const wrapper = document.createElement('div')
  wrapper.className = GeneralPromptStyles.promptWrapper
  const root = createRoot(wrapper)

  if (centered) {
    wrapper.classList.add(GeneralPromptStyles.centered)
    document.body.appendChild(wrapper)
  } else {
    if (position !== undefined) {
      const minimumHeight = 400
      if (window.innerHeight - position.top < minimumHeight) position.top = window.innerHeight - minimumHeight
      wrapper.style.top = position.top + 'px'
      wrapper.style.left = position.left + 'px'
    }
    document.body.appendChild(wrapper)
  }

  let resolveRef: Function

  const closePromise = new Promise<EmbedUnion>((resolve) => {
    resolveRef = resolve
  })

  const close = (embed?: EmbedUnion) => {
    root.unmount()
    wrapper.remove()
    view.focus()
    resolveRef(embed)
  }

  root.render(<PromptContent onSubmit={(embed) => close(embed)} preselectedIntegration={preselectedIntegration} />)

  if (position !== undefined) {
    adjustSidebarMaxHeight(wrapper, position)
    adjustHorizontalPosition(wrapper, position)
  }

  return closePromise
}

export const openInsertEmbedPromptAndReturnNode = async ({
  view,
  position,
  centered,
  preselectedIntegration,
}: {
  view: EditorView
  position?: Position
  centered?: boolean
  preselectedIntegration?: Integration
}) => {
  const embedCode = await openEmbedPrompt({
    view,
    position,
    centered,
    preselectedIntegration,
  })

  if (embedCode && isGithubEmbed(embedCode)) {
    const { url, type } = embedCode

    return view.state.schema.nodes.github_embed.create({ link: url, type, mode: 'frame' })
  } else if (embedCode && isJiraEmbed(embedCode)) {
    const { link, issueId, type } = embedCode

    return view.state.schema.nodes.jira_embed.create({ link, issueId, type, mode: 'frame' })
  } else if (embedCode) {
    const { html, url, meta, links, format, integrationRequired, provider } = embedCode

    const thumbnailUrl = (links?.thumbnail || []).length > 0 ? links.thumbnail[0].href : null

    return view.state.schema.nodes.iframe.createAndFill({
      srcdoc: html,
      url,
      title: meta?.title,
      description: meta?.description,
      thumbnailUrl,
      format,
      integrationRequired,
      provider,
    }) as ProseNode
  }
}
